import { Box, StyleProps, Text } from '@chakra-ui/react'
import { ReactNode } from 'react'

/** How to match and highlight: subsequence (chars in order) or substring (consecutive only). Use substring when filtering uses substring match. */
export type HighlightTextMatchMode = 'subsequence' | 'substring'

export interface HighlightTextProps extends StyleProps {
  /**
   * The text content to display and highlight
   */
  text: string
  /**
   * The search term to highlight in the text
   */
  searchTerm?: string
  /**
   * How to match: subsequence (chars in order) or substring (consecutive). Use substring to match PickListFilter/SelectInput substring filtering.
   * @default 'subsequence'
   */
  matchMode?: HighlightTextMatchMode
  /**
   * Whether the search is case sensitive
   * @default false
   */
  caseSensitive?: boolean
  /**
   * Custom component to render highlighted text
   */
  highlightComponent?: (text: string, index: number) => ReactNode
}

/**
 * Renders text content with optional styling. When no styling props are provided,
 * returns a plain span to inherit parent styles (e.g., fontSize from parent Box).
 * When styling props are provided, wraps in Text component to apply those styles.
 */
const renderTextOrSpan = (
  content: ReactNode,
  props: StyleProps,
  hasStylingProps: boolean,
) => {
  return hasStylingProps ? (
    <Text {...props}>{content}</Text>
  ) : (
    <span>{content}</span>
  )
}

/**
 * Highlights matching characters in the text based on the search query.
 * - subsequence: chars in order (e.g. "abc" matches "a b c")
 * - substring: consecutive run (matches PickListFilter/SelectInput substring filtering)
 */
export const HighlightText = ({
  text,
  searchTerm = '',
  matchMode = 'subsequence',
  caseSensitive = false,
  highlightComponent,
  ...props
}: HighlightTextProps) => {
  const hasStylingProps = Object.keys(props).length > 0

  if (!searchTerm) {
    return renderTextOrSpan(text, props, hasStylingProps)
  }

  const query = caseSensitive ? searchTerm : searchTerm.toLowerCase()
  const label = caseSensitive ? text : text.toLowerCase()

  if (matchMode === 'substring') {
    const start = label.indexOf(query)
    if (start === -1) {
      return renderTextOrSpan(text, props, hasStylingProps)
    }
    const end = start + query.length
    const highlightEl = highlightComponent || defaultHighlightComponent
    const content = (
      <span>
        {start > 0 && text.slice(0, start)}
        {text
          .slice(start, end)
          .split('')
          .map((ch, i) => highlightEl(ch, i))}
        {end < text.length && text.slice(end)}
      </span>
    )
    return renderTextOrSpan(content, props, hasStylingProps)
  }

  const matchIndices: number[] = []
  let queryIndex = 0
  for (
    let labelIndex = 0;
    labelIndex < label.length && queryIndex < query.length;
    labelIndex++
  ) {
    if (label[labelIndex] === query[queryIndex]) {
      matchIndices.push(labelIndex)
      queryIndex++
    }
  }

  if (matchIndices.length !== query.length) {
    return renderTextOrSpan(text, props, hasStylingProps)
  }

  const parts: ReactNode[] = []
  let lastIndex = 0

  matchIndices.forEach((matchIndex, i) => {
    if (matchIndex > lastIndex) {
      parts.push(text.slice(lastIndex, matchIndex))
    }

    const highlightElement = highlightComponent || defaultHighlightComponent
    parts.push(highlightElement(text[matchIndex], i))

    lastIndex = matchIndex + 1
  })

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  const content = <span>{parts}</span>

  return renderTextOrSpan(content, props, hasStylingProps)
}

const defaultHighlightComponent = (text: string, index: number) => (
  <Box
    key={`highlight-${index}`}
    as="span"
    fontWeight="700"
    color="presets.turquoise.60"
  >
    {text}
  </Box>
)
